import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  createNode,
  updateNode,
  deleteNode,
  suggestActionsForMilestone,
  addNodeUpdate,
  deleteNodeUpdate,
  createPlanningTodo,
  fetchPlanningTodos,
  fetchResources,
  createResource,
  createPlanSummary,
} from '../../actions/planningAction';
import { subNodeColor } from './canvas/canvasLayout';
import { focusLsKey } from './PlanningExecute';
import './PlanningDashboard.css';

function AgentIconBtn({ onClick }) {
  return (
    <button
      className="pd-agent-icon-btn"
      onClick={e => { e.stopPropagation(); onClick?.(); }}
      title="Focus AI chat on this node"
    >
      <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 0.5L7.15 4.85L11.5 6L7.15 7.15L6 11.5L4.85 7.15L0.5 6L4.85 4.85Z"
          fill="currentColor"
        />
        <circle cx="10" cy="2" r="0.9" fill="currentColor" fillOpacity="0.55" />
      </svg>
    </button>
  );
}

const STATUS_NEXT  = { standby: 'active', active: 'done', done: 'standby' };
const STATUS_LABEL = { standby: 'Standby', active: 'Active', done: 'Done' };
const CONNECTOR_COLORS   = [
  'rgba(241,117,1,0.5)',
  'rgba(52,211,153,0.5)',
  'rgba(251,191,36,0.5)',
  'rgba(248,113,113,0.5)',
  'rgba(96,165,250,0.5)',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function mProgress(m) {
  const actions = m.actions || [];
  const total   = actions.length;
  const done    = actions.filter(a => a.status === 'done').length;
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

function findNode(nodes, id) {
  for (const n of nodes) {
    if (n._id === id) return n;
    const a = findNode(n.actions || [], id);
    if (a) return a;
    const s = findNode(n.subNodes || [], id);
    if (s) return s;
  }
  return null;
}

function getChildren(node) {
  if (!node) return [];
  // A node may have both nested actions (.actions) and sub-nodes (.subNodes).
  // The server tree builder files every type:'action' child under .actions and
  // every other child under .subNodes, regardless of the parent's own type — so
  // an action can carry nested actions. Return both so nothing is hidden.
  return [...(node.actions || []), ...(node.subNodes || [])];
}

function findNodePath(milestones, targetId) {
  const tid = String(targetId).replace(/^id:/, '');
  function search(nodes, path) {
    for (const n of nodes) {
      const nid = String(n._id);
      if (nid === tid) return { node: n, path };
      const r = search(n.actions || [], [...path, nid])
             || search(n.subNodes || [], [...path, nid]);
      if (r) return r;
    }
    return null;
  }
  return search(milestones, []);
}

function rowLabel(level) {
  if (level === 0) return 'Milestones on the path';
  if (level === 1) return 'Actions for this milestone';
  return 'Nodes';
}

function addBtnLabel(level) {
  if (level === 0) return 'Milestone';
  if (level === 1) return 'Action';
  return 'Node';
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────

function DeleteConfirmModal({ node, onConfirm, onCancel, deleting }) {
  const preview = node.content.length > 80 ? node.content.slice(0, 80) + '…' : node.content;
  return (
    <div className="pd-delete-overlay" onClick={onCancel}>
      <div className="pd-delete-modal" onClick={e => e.stopPropagation()}>
        <div className="pd-delete-modal-icon">⚠</div>
        <h3 className="pd-delete-modal-title">Delete this node?</h3>
        <p className="pd-delete-modal-preview">"{preview}"</p>
        <p className="pd-delete-modal-warn">Child nodes will also be deleted. This cannot be undone.</p>
        <div className="pd-delete-modal-btns">
          <button className="pd-delete-modal-confirm" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete node'}
          </button>
          <button className="pd-delete-modal-cancel" onClick={onCancel} disabled={deleting}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Node Edit Panel ───────────────────────────────────────────────────────────

function NodeEditPanel({ panel, visionId, milestones, drillPath, onClose, onRefresh, onOpenChat }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mode, level, parentId, node } = panel;

  const isCreate = mode === 'create';
  const isDeep   = level >= 2;
  const variant  = level === 0 ? 'milestone' : level === 1 ? 'action' : 'deep';

  // Batch creation state (create mode only)
  const [batchItems,  setBatchItems]  = useState([]); // [{ id, tag, content }]
  const batchCounter                  = React.useRef(0);

  const [content, setContent] = useState(node?.content  || '');
  const [tag,     setTag]     = useState(node?.customType || '');
  const [status,  setStatus]  = useState(node?.status || 'standby');
  // Linked documents (resources) for this node
  const [resources,   setResources]   = useState([]);
  const [resLoading,  setResLoading]   = useState(false);
  // Todos linked to this node (from Execute)
  const [linkedTodos,    setLinkedTodos]    = useState([]);
  const [todosLoading,   setTodosLoading]   = useState(false);
  const [saving,         setSaving]         = useState(false);
  const [showDelete,     setShowDelete]     = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [updates,        setUpdates]        = useState(node?.updates || []);
  const [updateText,     setUpdateText]     = useState('');
  const [postingUpdate,  setPostingUpdate]  = useState(false);
  const [todoText,       setTodoText]       = useState('');
  const [todoAdded,      setTodoAdded]      = useState(false);

  const defaultLabel = level === 0 ? 'Milestone' : level === 1 ? 'Action' : 'Node';
  const variantLabel = isCreate ? defaultLabel : (node?.customType || defaultLabel);
  const parentNode   = parentId ? findNode(milestones, parentId) : null;


  const breadcrumb = drillPath.map(id => {
    const n = findNode(milestones, id);
    if (!n) return null;
    const t = n.content?.slice(0, 22) || '';
    return t.length < (n.content?.length || 0) ? t + '…' : t;
  }).filter(Boolean);

  const statusOptions = ['standby', 'active', 'done'];

  const addToBatch = () => {
    if (!content.trim()) return;
    setBatchItems(p => [...p, { id: ++batchCounter.current, tag: tag.trim(), content: content.trim() }]);
    setContent('');
    setTag('');
  };

  const removeFromBatch = (id) => setBatchItems(p => p.filter(i => i.id !== id));

  const buildPayload = (c, t) => {
    if (level === 0) return { type: 'milestone', content: c, customType: t };
    if (level === 1) return { type: 'action',    content: c, parentId, customType: t };
    return { type: 'node', content: c, parentId, customType: t };
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (isCreate) {
        const all = [...batchItems, ...(content.trim() ? [{ tag: tag.trim(), content: content.trim() }] : [])];
        if (!all.length) { setSaving(false); return; }
        for (const item of all) {
          await dispatch(createNode(visionId, buildPayload(item.content, item.tag)));
        }
      } else {
        const payload = { content: content.trim(), status, customType: tag.trim() };
        if (isDeep) payload.type = 'node';
        await dispatch(updateNode(node._id, payload));
      }
      onRefresh?.();
      onClose();
    } catch (_) {
      setSaving(false);
    }
  };

  const handleKey = e => {
    if (e.key === 'Escape') onClose();
    if (!isCreate && (e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
  };

  const handleInputKey = e => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addToBatch(); }
  };

  const handleDelete = async () => {
    if (deleting || !node?._id) return;
    setDeleting(true);
    try {
      await dispatch(deleteNode(node._id));
      onRefresh?.();
      onClose();
    } catch (_) {
      setDeleting(false);
    }
  };

  const postUpdate = async () => {
    if (!updateText.trim() || postingUpdate || !node?._id) return;
    const text = updateText.trim();
    setPostingUpdate(true);
    try {
      const res = await dispatch(addNodeUpdate(node._id, text));
      setUpdates(res.updates || []);
      setUpdateText('');
      onRefresh?.();
    } catch (_) {}
    setPostingUpdate(false);
  };

  const removeUpdate = async (updateId) => {
    if (!node?._id) return;
    try {
      const res = await dispatch(deleteNodeUpdate(node._id, updateId));
      setUpdates(res.updates || []);
      onRefresh?.();
    } catch (_) {}
  };

  const formatUpdateDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const addTodo = async () => {
    const text = todoText.trim();
    if (!text) return;
    setTodoText('');
    try {
      await dispatch(createPlanningTodo(visionId, {
        text,
        nodeId:      node?._id ? String(node._id) : undefined,
        nodeContent: node?.content ? node.content.slice(0, 80) : undefined,
      }));
      setTodoAdded(true);
      setTimeout(() => setTodoAdded(false), 2000);
    } catch (_) {}
  };

  // ── Linked documents (resources) ───────────────────────────────────────────
  useEffect(() => {
    if (isCreate || !node?._id) return;
    setResLoading(true);
    dispatch(fetchResources(visionId, String(node._id)))
      .then(d => setResources(d.resources || []))
      .catch(() => {})
      .finally(() => setResLoading(false));
  }, [isCreate, node?._id, visionId, dispatch]);

  // ── Linked todos (from Execute) ────────────────────────────────────────────
  useEffect(() => {
    if (isCreate || !node?._id) return;
    setTodosLoading(true);
    dispatch(fetchPlanningTodos(visionId))
      .then(d => {
        const nid = String(node._id);
        setLinkedTodos((d.todos || []).filter(t => String(t.nodeId) === nid && !t.archivedAt));
      })
      .catch(() => {})
      .finally(() => setTodosLoading(false));
  }, [isCreate, node?._id, visionId, dispatch]);

  const openDoc = (id) => navigate(`/dashboard/planning/${visionId}/doc/${id}`);
  const openTodo = (id) => navigate(`/dashboard/planning/${visionId}?view=execute&todo=${id}`);

  const newResource = async () => {
    if (!node?._id) return;
    try {
      const d = await dispatch(createResource(visionId, {
        title: 'Untitled',
        body: '',
        linkedNodeIds: [String(node._id)],
      }));
      openDoc(d.resource._id);
    } catch (_) {}
  };

  const totalToCreate = batchItems.length + (content.trim() ? 1 : 0);

  return (
    <div className={`pd-edit-panel pd-edit-panel--${variant}`} onKeyDown={handleKey}>

      {isCreate ? (
        /* ── CREATE MODE ── */
        <div className="pd-ep-create-body">

          {/* Header */}
          <div className="pd-ep-create-header">
            <div className="pd-ep-create-header-left">
              <span className={`pd-ep-create-type-chip pd-ep-create-type-chip--${variant}`}>
                {defaultLabel}
              </span>
              <h2 className="pd-ep-create-title">
                {level === 0 ? 'New Milestones' : level === 1 ? 'New Actions' : 'New Nodes'}
              </h2>
              {parentNode && (
                <p className="pd-ep-create-parent-line">
                  under <span className="pd-ep-create-parent-name">{parentNode.content}</span>
                </p>
              )}
              <p className="pd-ep-create-subtitle">Break it into smaller units: clearer to define, easier to act on.</p>
              <div className="pd-ep-assist-block">
                <button className="pd-ep-assist-btn" onClick={() => onOpenChat?.(parentNode || null)}>
                  ✦ Ask Dmyor Assist
                </button>
                <p className="pd-ep-assist-desc">Not sure how to break this down? Chat with Dmyor Assist and it'll suggest what to add.</p>
              </div>
            </div>
            <button className="pd-ep-close" onClick={onClose} title="Esc">×</button>
          </div>

          {/* Queued items */}
          <div className="pd-ep-batch-scroll">
            {batchItems.length === 0 ? (
              <div className="pd-ep-batch-empty">
                Write below and hit <strong>Queue</strong>. Add as many as you want, then create all at once.
              </div>
            ) : (
              <>
                <div className="pd-ep-batch-count">
                  {batchItems.length} queued · ready to create
                </div>
                <div className="pd-ep-batch-list">
                  {batchItems.map((item) => (
                    <div key={item.id} className="pd-ep-batch-item">
                      <div className="pd-ep-batch-text">
                        {item.tag && <span className="pd-ep-batch-tag">{item.tag}</span>}
                        <span className="pd-ep-batch-content">{item.content}</span>
                      </div>
                      <button className="pd-ep-batch-remove" onClick={() => removeFromBatch(item.id)} title="Remove">×</button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Input — pinned bottom */}
          <div className="pd-ep-create-input-block">
            <span className="pd-ep-field-label">
              {level === 0 ? 'Milestone' : level === 1 ? 'Action' : 'Node'}
            </span>
            <textarea
              className="pd-ep-create-content-input"
              placeholder={
                level === 0 ? 'Describe the milestone…'
                : level === 1 ? 'What needs to be done?'
                : `Describe this ${tag || 'node'}…`
              }
              value={content}
              onChange={e => setContent(e.target.value)}
              onKeyDown={handleInputKey}
              rows={3}
              autoFocus
            />
            <div className="pd-ep-create-row2">
              <input
                className="pd-ep-create-label-input"
                placeholder={
                  level === 0 ? 'Custom label — e.g. Phase (optional)'
                  : level === 1 ? 'Custom label — e.g. Task (optional)'
                  : 'Label — e.g. Risk, Blocker, Insight…'
                }
                value={tag}
                onChange={e => setTag(e.target.value)}
                maxLength={60}
                onKeyDown={handleInputKey}
              />
              <button className="pd-ep-queue-btn" onClick={addToBatch} disabled={!content.trim()}>
                Queue <kbd className="pd-ep-queue-hint">⌘↵</kbd>
              </button>
            </div>
          </div>

          {/* Footer inside create body */}
          <div className="pd-ep-footer">
            <button
              className="pd-ep-save-btn"
              onClick={save}
              disabled={totalToCreate === 0 || saving}
            >
              {saving ? 'Creating…' : `Create ${totalToCreate} ${defaultLabel}${totalToCreate !== 1 ? 's' : ''}`}
            </button>
            <button className="pd-ep-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
          </div>
        </div>

      ) : (
        /* ── EDIT MODE ── */
        <>
          <div className="pd-ep-header">
            <span className="pd-ep-variant-label">{variantLabel}</span>
            <button className="pd-ep-close" onClick={onClose} title="Close (Esc)">×</button>
          </div>

          {breadcrumb.length > 0 && (
            <div className="pd-ep-breadcrumb">
              {breadcrumb.map((crumb, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="pd-ep-crumb-sep">›</span>}
                  <span className="pd-ep-crumb-item">{crumb}</span>
                </React.Fragment>
              ))}
            </div>
          )}

          <div className="pd-ep-body pd-ep-body--cols">
            <div className="pd-ep-col-main">
              <div className="pd-ep-section">
                <div className="pd-ep-section-label">Label</div>
                <input
                  className="pd-input"
                  placeholder={
                    level === 0 ? 'e.g. Goal, Objective, Phase... (default: Milestone)'
                    : level === 1 ? 'e.g. Step, Task, Sprint... (default: Action)'
                    : 'e.g. Risk, Blocker, Insight, Question...'
                  }
                  value={tag}
                  onChange={e => setTag(e.target.value)}
                  maxLength={60}
                />
                <p className="pd-ep-node-hint">
                  {level === 0 ? 'Rename this milestone anything. Blank defaults to "Milestone".'
                   : level === 1 ? 'Rename this action anything. Blank defaults to "Action".'
                   : 'Label this node anything. Use it to mark what this means to you.'}
                </p>
              </div>

              <div className="pd-ep-section">
                <div className="pd-ep-section-label">Status</div>
                <div className="pd-ep-status-row">
                  {statusOptions.map(s => (
                    <button
                      key={s}
                      className={`pd-ep-status-pill pd-ep-status-pill--${s}${status === s ? ' pd-ep-status-pill--on' : ''}`}
                      onClick={() => setStatus(s)}
                    >
                      <span className="pd-ep-status-dot" />
                      {STATUS_LABEL[s]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pd-ep-section">
                <div className="pd-ep-section-label">Description</div>
                <textarea
                  className="pd-ep-textarea"
                  placeholder={
                    level === 0 ? 'Describe this milestone...'
                    : level === 1 ? 'What needs to be done?'
                    : `Describe this ${tag || 'node'}...`
                  }
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  autoFocus
                />
                <div className="pd-ep-hint">⌘↵ save · Esc close</div>
              </div>

              <div className="pd-ep-section">
                <div className="pd-ep-section-label">Add to today's list</div>
                <div className="pd-ep-todo-row">
                  <input
                    className="pd-input"
                    placeholder="Quick todo for today…"
                    value={todoText}
                    onChange={e => setTodoText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTodo(); } }}
                    maxLength={200}
                  />
                  <button className="pd-ep-todo-add" onClick={addTodo} disabled={!todoText.trim()}>
                    {todoAdded ? '✓ Added' : 'Add'}
                  </button>
                </div>
                <p className="pd-ep-node-hint">Shows up in Execute → Today's list.</p>
              </div>

              <div className="pd-ep-section">
                <div className="pd-ep-section-label">Updates</div>
                <div className="pd-ep-update-form">
                  <textarea
                    className="pd-ep-update-input"
                    placeholder="What's the latest on this?"
                    value={updateText}
                    onChange={e => setUpdateText(e.target.value)}
                    onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') postUpdate(); }}
                  />
                  <button className="pd-ep-update-post" onClick={postUpdate} disabled={!updateText.trim() || postingUpdate}>
                    {postingUpdate ? '…' : 'Post'}
                  </button>
                </div>
                {updates.length > 0 && (
                  <div className="pd-ep-updates-list">
                    {[...updates].reverse().map(u => (
                      <div key={u._id} className="pd-ep-update-item">
                        <p className="pd-ep-update-text">{u.text}</p>
                        <div className="pd-ep-update-meta">
                          <span className="pd-ep-update-date">{formatUpdateDate(u.createdAt)}</span>
                          <button className="pd-ep-update-del" onClick={() => removeUpdate(u._id)}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="pd-ep-col-ws">
              <div className="pd-ep-ws-head">
                <div className="pd-ep-section-label pd-ep-ws-title">◆ Documents</div>
                <button className="pd-ep-ws-draft" onClick={newResource}>+ New document</button>
              </div>
              {resLoading ? (
                <div className="pd-ep-docs-empty">Loading…</div>
              ) : resources.length === 0 ? (
                <div className="pd-ep-docs-empty">
                  No documents linked to this node yet.<br />
                  Create one to build out the actual work.
                </div>
              ) : (
                <div className="pd-ep-docs-list">
                  {resources.map(r => (
                    <button key={r._id} className="pd-ep-doc-card" onClick={() => openDoc(r._id)}>
                      <span className="pd-ep-doc-title">{r.title || 'Untitled'}</span>
                      <span className="pd-ep-doc-snippet">
                        {(r.body || '').replace(/[#*`>|\-[\]]/g, '').slice(0, 90).trim() || 'Empty document'}
                      </span>
                      {(r.linkedNodeIds || []).length > 1 && (
                        <span className="pd-ep-doc-shared">↔ shared with {r.linkedNodeIds.length - 1} other node{r.linkedNodeIds.length - 1 > 1 ? 's' : ''}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <div className="pd-ep-hint">Documents live in the Library and can link to many nodes.</div>

              {/* Linked todos from Execute */}
              <div className="pd-ep-todos">
                <div className="pd-ep-section-label pd-ep-ws-title">✓ Todos</div>
                {todosLoading ? (
                  <div className="pd-ep-docs-empty">Loading…</div>
                ) : linkedTodos.length === 0 ? (
                  <div className="pd-ep-docs-empty">No todos linked to this node yet.</div>
                ) : (
                  <div className="pd-ep-todos-list">
                    {linkedTodos.map(t => (
                      <button
                        key={t._id}
                        className={`pd-ep-todo-row${t.done ? ' pd-ep-todo-row--done' : ''}`}
                        onClick={() => openTodo(t._id)}
                        title="Open in Execute"
                      >
                        <span className={`pd-ep-todo-check${t.done ? ' pd-ep-todo-check--on' : ''}`} />
                        <span className="pd-ep-todo-text">{t.text}</span>
                        {t.due && <span className="pd-ep-todo-due">{new Date(t.due).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="pd-ep-footer">
            <button className="pd-ep-save-btn" onClick={save} disabled={!content.trim() || saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="pd-ep-cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
            {node && (
              <button className="pd-ep-delete-btn" onClick={() => setShowDelete(true)} disabled={saving || deleting}>
                Delete node
              </button>
            )}
          </div>

          {showDelete && (
            <DeleteConfirmModal
              node={node}
              onConfirm={handleDelete}
              onCancel={() => setShowDelete(false)}
              deleting={deleting}
            />
          )}
        </>
      )}
    </div>
  );
}

// ── Cards ─────────────────────────────────────────────────────────────────────

function MilestoneCard({ milestone, selected, highlighted, hasDocs, onClick, onEdit, visionId, onRefresh, onOpenChat }) {
  const dispatch = useDispatch();
  const [suggestions,  setSuggestions] = useState([]);
  const [localStatus,  setLocalStatus] = useState(milestone.status || 'standby');
  const [pinned,       setPinned]      = useState(() => {
    const ids = JSON.parse(localStorage.getItem(focusLsKey(visionId)) || '[]');
    return ids.includes(String(milestone._id));
  });

  const togglePin = (e) => {
    e.stopPropagation();
    const key  = focusLsKey(visionId);
    const ids  = JSON.parse(localStorage.getItem(key) || '[]');
    const nid  = String(milestone._id);
    const next = pinned ? ids.filter(id => id !== nid) : [...ids, nid];
    localStorage.setItem(key, JSON.stringify(next));
    setPinned(!pinned);
  };

  useEffect(() => { setLocalStatus(milestone.status || 'standby'); }, [milestone.status]);

  const { done, total, pct } = mProgress(milestone);

  const cycleStatus = async (e) => {
    e.stopPropagation();
    const next = STATUS_NEXT[localStatus] || 'standby';
    const prev = localStatus;
    setLocalStatus(next);
    try {
      await dispatch(updateNode(milestone._id, { status: next }));
      onRefresh?.();
    } catch (_) { setLocalStatus(prev); }
  };

  const getSuggestions = async (e) => {
    e.stopPropagation();
    try {
      const res = await dispatch(suggestActionsForMilestone(milestone._id));
      setSuggestions(res?.suggestions || []);
    } catch (_) {}
  };

  const acceptSug = async (sug) => {
    await dispatch(createNode(visionId, { type: 'action', content: sug.content, parentId: milestone._id }));
    setSuggestions(prev => prev.filter(s => s !== sug));
    onRefresh?.();
  };

  return (
    <div className={`pd-ms-card${selected ? ' pd-ms-card--selected' : ''}${highlighted ? ' pd-card--highlighted' : ''}${localStatus === 'done' ? ' pd-ms-card--done' : ''}`} onClick={onClick}>
      <div className="pd-ms-card-eyebrow">
        <span>{milestone.customType || 'Milestone'}</span>
        <span className={`pd-ms-card-status pd-ms-card-status--${localStatus}`} />
      </div>

      <p className="pd-ms-card-content">{milestone.content}</p>

      {total > 0 && (
        <div className="pd-ms-card-prog">
          <div className="pd-ms-card-prog-track">
            <div className="pd-ms-card-prog-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="pd-ms-card-prog-label">{done}/{total}</span>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="pd-ms-sug-list" onClick={e => e.stopPropagation()}>
          {suggestions.slice(0, 3).map((sug, i) => (
            <div key={i} className="pd-ms-sug-row">
              <span className="pd-ms-sug-text">{sug.content}</span>
              <button className="pd-ms-sug-add" onClick={() => acceptSug(sug)}>+</button>
              <button className="pd-ms-sug-dismiss" onClick={() => setSuggestions(p => p.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
        </div>
      )}

      <div className="pd-ms-card-footer" onClick={e => e.stopPropagation()}>
        <button className={`pd-ms-card-cycle pd-ms-card-cycle--${localStatus}`} onClick={cycleStatus}>
          {STATUS_LABEL[localStatus]}
        </button>
        <div className="pd-card-actions">
          {hasDocs && <i className="bx bxs-file-doc pd-doc-indicator" title="Has documents" />}
          <button
            className={`pd-pin-btn${pinned ? ' pd-pin-btn--on' : ''}`}
            onClick={togglePin}
            title={pinned ? 'Unpin from today' : 'Pin to today'}
          >
            {pinned ? '★' : '☆'}
          </button>
          <button className="pd-edit-icon-btn" onClick={e => { e.stopPropagation(); onEdit?.(); }} title="Edit">
            <i className="bx bx-pencil" />
          </button>
          <AgentIconBtn onClick={onOpenChat} />
        </div>
      </div>
    </div>
  );
}

function ActionCard({ action, selected, highlighted, hasDocs, childCount, onClick, onEdit, onRefresh, visionId, onOpenChat }) {
  const dispatch = useDispatch();
  const subCount = childCount ?? (action.subNodes?.length || 0);
  const [localStatus, setLocalStatus] = useState(action.status || 'standby');
  const [pinned,      setPinned]      = useState(() => {
    if (!visionId) return false;
    const ids = JSON.parse(localStorage.getItem(focusLsKey(visionId)) || '[]');
    return ids.includes(String(action._id));
  });

  const togglePin = (e) => {
    e.stopPropagation();
    if (!visionId) return;
    const key  = focusLsKey(visionId);
    const ids  = JSON.parse(localStorage.getItem(key) || '[]');
    const nid  = String(action._id);
    const next = pinned ? ids.filter(id => id !== nid) : [...ids, nid];
    localStorage.setItem(key, JSON.stringify(next));
    setPinned(!pinned);
  };

  useEffect(() => { setLocalStatus(action.status || 'standby'); }, [action.status]);

  const isDone = localStatus === 'done';

  const cycleStatus = async (e) => {
    e.stopPropagation();
    const next = STATUS_NEXT[localStatus] || 'standby';
    const prev = localStatus;
    setLocalStatus(next);
    try {
      await dispatch(updateNode(action._id, { status: next }));
      onRefresh?.();
    } catch (_) { setLocalStatus(prev); }
  };

  return (
    <div
      className={`pd-action-card${selected ? ' pd-action-card--selected' : ''}${isDone ? ' pd-action-card--done' : ''}${highlighted ? ' pd-card--highlighted' : ''}`}
      onClick={onClick}
    >
      <div className="pd-ms-card-eyebrow">
        <span>{action.customType || 'Action'}</span>
      </div>
      <div className="pd-action-card-top">
        <span className="pd-action-card-text">{action.content}</span>
      </div>
      <div className="pd-action-card-footer">
        <span className={`pd-action-status-badge pd-action-status-badge--${localStatus}`} onClick={e => { e.stopPropagation(); cycleStatus(e); }}>
          {STATUS_LABEL[localStatus]}
        </span>
        <div className="pd-card-actions">
          {subCount > 0 && <span className="pd-action-sub-badge">{subCount} node{subCount > 1 ? 's' : ''}</span>}
          {hasDocs && <i className="bx bxs-file-doc pd-doc-indicator" title="Has documents" />}
          <button
            className={`pd-pin-btn${pinned ? ' pd-pin-btn--on' : ''}`}
            onClick={togglePin}
            title={pinned ? 'Unpin from today' : 'Pin to today'}
          >
            {pinned ? '★' : '☆'}
          </button>
          <button className="pd-edit-icon-btn" onClick={e => { e.stopPropagation(); onEdit?.(); }} title="Edit">
            <i className="bx bx-pencil" />
          </button>
          <AgentIconBtn onClick={onOpenChat} />
        </div>
      </div>
    </div>
  );
}

function DeepCard({ node, selected, highlighted, hasDocs, childCount: childCountProp, onClick, onEdit, onRefresh, onOpenChat }) {
  const dispatch   = useDispatch();
  const color      = subNodeColor(node.type);
  const label      = node.customType || node.type;
  const childCount = childCountProp ?? ((node.actions || []).length + (node.subNodes || []).length);
  const [localStatus, setLocalStatus] = useState(node.status || 'standby');

  useEffect(() => { setLocalStatus(node.status || 'standby'); }, [node.status]);

  const cycleStatus = async (e) => {
    e.stopPropagation();
    const next = STATUS_NEXT[localStatus] || 'standby';
    const prev = localStatus;
    setLocalStatus(next);
    try {
      await dispatch(updateNode(node._id, { status: next }));
      onRefresh?.();
    } catch (_) { setLocalStatus(prev); }
  };

  return (
    <div
      className={`pd-sub-card${selected ? ' pd-sub-card--selected' : ''}${highlighted ? ' pd-card--highlighted' : ''}${localStatus === 'done' ? ' pd-sub-card--done' : ''}`}
      style={{ '--sub-color': color }}
      onClick={onClick}
    >
      <span className="pd-sub-card-type" style={{ color }}>{label}</span>
      <p className="pd-sub-card-content">{node.content}</p>
      {childCount > 0 && (
        <span className="pd-sub-child-badge">{childCount} child{childCount > 1 ? 'ren' : ''}</span>
      )}
      <div className="pd-sub-card-row">
        <span
          className={`pd-action-status-badge pd-action-status-badge--${localStatus}`}
          onClick={cycleStatus}
        >
          {STATUS_LABEL[localStatus]}
        </span>
        <div className="pd-card-actions">
          {hasDocs && <i className="bx bxs-file-doc pd-doc-indicator" title="Has documents" />}
          <button className="pd-edit-icon-btn" onClick={e => { e.stopPropagation(); onEdit?.(); }} title="Edit">
            <i className="bx bx-pencil" />
          </button>
          <AgentIconBtn onClick={onOpenChat} />
        </div>
      </div>
    </div>
  );
}

// ── Branch Connector ───────────────────────────────────────────────────────────

const CONN_H = 52;

function BranchConnector({ sourcePx, childCenters, color }) {
  if (sourcePx === null || sourcePx === undefined) return <div style={{ height: CONN_H }} />;

  const valid  = (childCenters || []).filter(x => x !== null && x !== undefined);
  const midY   = CONN_H / 2;
  const stroke = color || 'rgba(241,117,1,0.5)';
  const minX   = valid.length ? Math.min(sourcePx, ...valid) : sourcePx;
  const maxX   = valid.length ? Math.max(sourcePx, ...valid) : sourcePx;

  return (
    <div className="pd-connector" style={{ height: CONN_H }}>
      <svg
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: CONN_H }}
        overflow="visible"
      >
        <line x1={sourcePx} y1={0} x2={sourcePx} y2={midY} stroke={stroke} strokeWidth={1.5} strokeDasharray="5 4" />
        {valid.length > 0 && (
          <line x1={minX} y1={midY} x2={maxX} y2={midY} stroke={stroke} strokeWidth={1.5} strokeDasharray="5 4" />
        )}
        {valid.map((cx, i) => (
          <line key={i} x1={cx} y1={midY} x2={cx} y2={CONN_H} stroke={stroke} strokeWidth={1.5} strokeDasharray="5 4" />
        ))}
        {valid.map((cx, i) => (
          <circle key={`dot-${i}`} cx={cx} cy={CONN_H} r={2.5} fill={stroke} />
        ))}
        <circle cx={sourcePx} cy={0} r={2.5} fill={stroke} />
      </svg>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export default function PlanningDashboard({ vision, milestones, visionId, onRefresh, theme = 'dark', focusNode, onOpenChatWithNode }) {
  const dispatch = useDispatch();
  const [drillPath,       setDrillPath]       = useState([]);
  const [editPanel,       setEditPanel]       = useState(null);
  const [connectors,      setConnectors]      = useState([]);
  const [highlightNodeId, setHighlightNodeId] = useState(null);
  const [visionConnector, setVisionConnector] = useState(null);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchOpen,      setSearchOpen]      = useState(false);
  const [docNodeIds,      setDocNodeIds]      = useState(() => new Set());
  const [summaryState,    setSummaryState]    = useState('idle'); // idle | working | done
  const [summaryDoc,      setSummaryDoc]      = useState(null);
  const [summaryUpdated,  setSummaryUpdated]  = useState(false);
  const navigate = useNavigate();

  // Which nodes have at least one linked document (for the resource icon)
  useEffect(() => {
    let cancelled = false;
    dispatch(fetchResources(visionId))
      .then(d => {
        if (cancelled) return;
        const ids = new Set();
        (d.resources || []).forEach(r => (r.linkedNodeIds || []).forEach(id => ids.add(String(id))));
        setDocNodeIds(ids);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [visionId, dispatch, milestones]);

  const makePlanSummary = async () => {
    if (summaryState === 'working') return;
    setSummaryState('working');
    try {
      const d = await dispatch(createPlanSummary(visionId));
      setSummaryDoc(d.resource);
      setSummaryUpdated(!!d.updated);
      setSummaryState('done');
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not create plan summary.');
      setSummaryState('idle');
    }
  };

  const rightRef      = useRef(null);
  const cardRefs      = useRef({});
  const rowScrollRefs = useRef({});
  const visionCardRef = useRef(null);

  const cardCenter = useCallback((el) => {
    if (!el || !rightRef.current) return null;
    const r    = el.getBoundingClientRect();
    const base = rightRef.current.getBoundingClientRect();
    return r.left - base.left + r.width / 2;
  }, []);

  const recomputeConnectors = useCallback(() => {
    const next = [];
    for (let k = 0; k < drillPath.length; k++) {
      const id         = drillPath[k];
      const sourcePx   = cardCenter(cardRefs.current[id]);
      const parent     = findNode(milestones, id);
      const children   = getChildren(parent);
      const childCenters = children
        .map(c => cardCenter(cardRefs.current[String(c._id)]))
        .filter(x => x !== null && x !== undefined);
      next.push({ sourcePx, childCenters, color: CONNECTOR_COLORS[k % CONNECTOR_COLORS.length] });
    }
    setConnectors(next);
  }, [drillPath, milestones, cardCenter]);

  useEffect(() => {
    if (drillPath.length === 0) { setConnectors([]); return; }
    const r1 = requestAnimationFrame(() => requestAnimationFrame(recomputeConnectors));
    // Fallback recomputes: when new cards mount (e.g. after AI applies nodes),
    // their refs may not be attached during the rAF pass above. Re-measure a
    // few times so connector lines catch up without needing a page reload.
    const t1 = setTimeout(recomputeConnectors, 150);
    const t2 = setTimeout(recomputeConnectors, 450);
    return () => { cancelAnimationFrame(r1); clearTimeout(t1); clearTimeout(t2); };
  }, [recomputeConnectors]);

  useEffect(() => {
    const rows = Object.values(rowScrollRefs.current).filter(Boolean);
    rows.forEach(el => el.addEventListener('scroll', recomputeConnectors, { passive: true }));
    return () => rows.forEach(el => el.removeEventListener('scroll', recomputeConnectors));
  }, [recomputeConnectors]);

  const recomputeVisionConnector = useCallback(() => {
    if (!visionCardRef.current || !rightRef.current) return;
    const sourcePx = cardCenter(visionCardRef.current);
    const childCenters = milestones
      .map(m => cardCenter(cardRefs.current[String(m._id)]))
      .filter(x => x !== null && x !== undefined);
    setVisionConnector(sourcePx !== null ? { sourcePx, childCenters } : null);
  }, [milestones, cardCenter]);

  useEffect(() => {
    const r = requestAnimationFrame(() => requestAnimationFrame(recomputeVisionConnector));
    return () => cancelAnimationFrame(r);
  }, [recomputeVisionConnector]);

  useEffect(() => {
    const msRow = rowScrollRefs.current[0];
    if (!msRow) return;
    msRow.addEventListener('scroll', recomputeVisionConnector, { passive: true });
    return () => msRow.removeEventListener('scroll', recomputeVisionConnector);
  }, [recomputeVisionConnector]);

  const focusNodeById = useCallback((id) => {
    const result = findNodePath(milestones, id);
    if (!result) return;
    const { node, path } = result;
    const nid = String(node._id);
    setDrillPath(path);
    // Navigate + highlight only — do NOT auto-open the edit panel.
    setHighlightNodeId(nid);
    requestAnimationFrame(() => {
      cardRefs.current[nid]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  }, [milestones]);

  useEffect(() => {
    if (!focusNode?.id || !milestones.length) return;
    focusNodeById(focusNode.id);
  }, [focusNode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flat searchable node list
  const allNodes = useMemo(() => {
    const list = [];
    for (const m of milestones) {
      list.push({ _id: m._id, type: 'milestone', content: m.content || m.title || '' });
      for (const a of (m.actions || [])) {
        list.push({ _id: a._id, type: 'action', content: a.content || '', parent: m.content });
        for (const s of (a.subNodes || [])) {
          list.push({ _id: s._id, type: s.type || 'node', content: s.content || '', parent: a.content });
        }
      }
    }
    return list;
  }, [milestones]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allNodes.filter(n => n.content.toLowerCase().includes(q)).slice(0, 10);
  }, [searchQuery, allNodes]);

  const handleSearchSelect = (id) => {
    setSearchOpen(false);
    setSearchQuery('');
    focusNodeById(id);
  };

  const selectAtLevel = (level, id) => {
    setDrillPath(prev => {
      if (prev[level] === id) return prev.slice(0, level);
      return [...prev.slice(0, level), id];
    });
  };

  const openCreate = (level) => {
    setEditPanel({
      mode:     'create',
      level,
      parentId: level === 0 ? null : drillPath[level - 1],
      node:     null,
    });
  };

  const openEdit = (node, level) => {
    setEditPanel({ mode: 'edit', level, parentId: null, node });
  };

  // Stats
  const totalActions = milestones.reduce((s, m) => s + (m.actions?.length || 0), 0);
  const doneActions  = milestones.reduce((s, m) => s + (m.actions?.filter(a => a.status === 'done').length || 0), 0);
  const overallPct   = totalActions ? Math.round((doneActions / totalActions) * 100) : 0;

  const expandAll = () => {
    const path = [];
    const m = milestones[0];
    if (!m) return;
    path.push(m._id);
    const a = (m.actions || [])[0];
    if (a) path.push(a._id);
    setDrillPath(path);
  };

  const isExpanded = drillPath.length > 0;

  // Rows
  const rows = Array.from({ length: drillPath.length + 1 }, (_, level) => {
    if (level === 0) return { level, nodes: milestones, parentNode: null };
    const parentNode = findNode(milestones, drillPath[level - 1]);
    return { level, nodes: getChildren(parentNode), parentNode };
  });

  const renderCard = (node, level) => {
    const nid        = String(node._id);
    const isSelected  = drillPath[level] === node._id;
    const highlighted = highlightNodeId === nid;
    const handleClick = () => {
      if (highlighted) setHighlightNodeId(null);
      selectAtLevel(level, node._id);
    };
    const handleEdit = () => openEdit(node, level);
    const setRef      = el => { cardRefs.current[nid] = el; };
    const hasDocs     = docNodeIds.has(nid);
    const childCount  = (node.actions?.length || 0) + (node.subNodes?.length || 0);

    if (node.type === 'milestone') {
      return (
        <div key={node._id} ref={setRef}>
          <MilestoneCard
            milestone={node}
            selected={isSelected}
            highlighted={highlighted}
            hasDocs={hasDocs}
            childCount={childCount}
            onClick={handleClick}
            onEdit={handleEdit}
            visionId={visionId}
            onRefresh={onRefresh}
            onOpenChat={() => onOpenChatWithNode?.(node)}
          />
        </div>
      );
    }
    if (node.type === 'action') {
      return (
        <div key={node._id} ref={setRef}>
          <ActionCard
            action={node}
            selected={isSelected}
            highlighted={highlighted}
            hasDocs={hasDocs}
            childCount={childCount}
            onClick={handleClick}
            onEdit={handleEdit}
            onRefresh={onRefresh}
            visionId={visionId}
            onOpenChat={() => onOpenChatWithNode?.(node)}
          />
        </div>
      );
    }
    return (
      <div key={node._id} ref={setRef}>
        <DeepCard
          node={node}
          selected={isSelected}
          highlighted={highlighted}
          hasDocs={hasDocs}
          childCount={childCount}
          onClick={handleClick}
          onEdit={handleEdit}
          onRefresh={onRefresh}
          onOpenChat={() => onOpenChatWithNode?.(node)}
        />
      </div>
    );
  };

  const addCardCls = (level) =>
    `pd-add-card pd-add-card--${level === 0 ? 'ms' : level === 1 ? 'action' : 'sub'}`;

  return (
    <div className={`pd-wrap pd-wrap--${theme}`}>
      {summaryState !== 'idle' && (
        <div className="pd-summary-overlay" onMouseDown={() => summaryState === 'done' && setSummaryState('idle')}>
          {summaryState === 'working' ? (
            <div className="pd-summary-card" onMouseDown={e => e.stopPropagation()}>
              <span className="pd-summary-spark">✦</span>
              <div className="pd-summary-text">Dmyor Assist is composing your plan summary…</div>
              <div className="pd-summary-sub">Reading every milestone, action and note across your plan</div>
            </div>
          ) : (
            <div className="pd-summary-card pd-summary-card--done" onMouseDown={e => e.stopPropagation()}>
              <span className="pd-summary-tick" />
              <div className="pd-summary-text">Your plan summary is ready</div>
              <div className="pd-summary-sub">"{summaryDoc?.title}" was {summaryUpdated ? 'updated in' : 'added to'} your Library.</div>
              <div className="pd-summary-actions">
                <button className="pd-summary-secondary" onClick={() => setSummaryState('idle')}>Keep planning</button>
                <button
                  className="pd-summary-primary"
                  onClick={() => navigate(`/dashboard/planning/${visionId}/doc/${summaryDoc._id}`)}
                >
                  Open document →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Right: Planner ── */}
      <div className={`pd-right${editPanel ? ' pd-right--narrow' : ''}`} ref={rightRef}>
        {milestones.length > 0 && (
          <div className="pd-planner-toolbar">
            <div className="pd-search-wrap">
              <input
                className="pd-search-input"
                placeholder="Search nodes…"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 160)}
              />
              {searchOpen && searchResults.length > 0 && (
                <div className="pd-search-results">
                  {searchResults.map(n => (
                    <button
                      key={n._id}
                      type="button"
                      className="pd-search-result"
                      onMouseDown={(e) => { e.preventDefault(); handleSearchSelect(n._id); }}
                    >
                      <span className={`pd-search-type pd-search-type--${n.type}`}>{n.type}</span>
                      <span className="pd-search-content">{n.content}</span>
                      {n.parent && <span className="pd-search-parent">in {n.parent}</span>}
                    </button>
                  ))}
                </div>
              )}
              {searchOpen && searchQuery.trim() && searchResults.length === 0 && (
                <div className="pd-search-results">
                  <div className="pd-search-empty">No matches</div>
                </div>
              )}
            </div>
            <div className="pd-toolbar-right">
              <button
                className="pd-expand-btn"
                onClick={isExpanded ? () => setDrillPath([]) : expandAll}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </button>
              <button
                className="pd-summary-btn"
                onClick={makePlanSummary}
                disabled={summaryState === 'working' || allNodes.length < 5}
                title={allNodes.length < 5 ? 'Build out your plan first (at least 5 nodes)' : 'Generate a plan summary document'}
              >
                ✦ Plan Summary
              </button>
            </div>
          </div>
        )}

        {/* Vision root card */}
        <div className="pd-vision-root-row">
          <div className="pd-vision-root-card" ref={visionCardRef}>
            <div className="pd-ms-card-eyebrow">
              <span>Vision</span>
              {vision?.timeframe && (
                <span className="pd-vision-dur">{vision.timeframe} Year vision</span>
              )}
            </div>
            <p className="pd-ms-card-content">{vision?.title}</p>
            {vision?.timeframe && vision?.createdAt && (() => {
              const MS = 86400000;
              const total = vision.timeframe * 365;
              const passed = Math.floor((Date.now() - new Date(vision.createdAt)) / MS);
              const left = Math.max(total - passed, 0);
              const pct = Math.min(Math.max((passed / total) * 100, 0), 100);
              return (
                <div className="pd-vision-time">
                  <div className="pd-vision-time-track">
                    <div className="pd-vision-time-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="pd-vision-time-meta">
                    <span>{left.toLocaleString()} days left</span>
                    <span>{Math.round(pct)}%</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {milestones.length > 0 && visionConnector && (
          <BranchConnector
            sourcePx={visionConnector.sourcePx}
            childCenters={visionConnector.childCenters}
            color="rgba(241,117,1,0.35)"
          />
        )}

        {rows.map(({ level, nodes }, rowIdx) => (
          <React.Fragment key={level}>
            {rowIdx > 0 && (
              <BranchConnector
                sourcePx={connectors[rowIdx - 1]?.sourcePx ?? null}
                childCenters={connectors[rowIdx - 1]?.childCenters ?? []}
                color={connectors[rowIdx - 1]?.color}
              />
            )}

            <div className="pd-section">
              <div className="pd-section-head">
                <span className="pd-section-label">{rowLabel(level)}</span>
                <span className="pd-section-count">{nodes.length}</span>
              </div>
              <div
                className="pd-cards-row"
                ref={el => { rowScrollRefs.current[level] = el; }}
              >
                {nodes.map(node => renderCard(node, level))}
                <button className={addCardCls(level)} onClick={() => openCreate(level)}>
                  <span className="pd-add-card-icon">+</span>
                  <span>{addBtnLabel(level)}</span>
                </button>
              </div>
            </div>
          </React.Fragment>
        ))}

        {milestones.length === 0 && (
          <div className="pd-empty">
            <div className="pd-empty-icon">◎</div>
            <p className="pd-empty-title">No milestones yet</p>
            <p className="pd-empty-sub">Add the first one above to start planning.</p>
          </div>
        )}
      </div>

      {/* ── Edit / Create panel (overlay drawer) ── */}
      {editPanel && (
        <div className="pd-ep-overlay" onMouseDown={() => setEditPanel(null)}>
          <div className="pd-ep-shell" onMouseDown={e => e.stopPropagation()}>
            <NodeEditPanel
              panel={editPanel}
              visionId={visionId}
              milestones={milestones}
              drillPath={drillPath}
              onClose={() => setEditPanel(null)}
              onRefresh={onRefresh}
              onOpenChat={(node) => { setEditPanel(null); onOpenChatWithNode?.(node || null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
